import { Delete, Edit } from "@mui/icons-material";
import { Component } from "@pimo/pimo-app-builder";
import React, { HTMLAttributes, useState } from "react";

import { useEditorHighlightColor } from "./get-editor-highlight-color";
import { DragDropLayoutComponentLayoutProps } from "./types";

export const Column: React.FC<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: Component<any, any, any, any, DragDropLayoutComponentLayoutProps>;
  onComponentDropped: (componentId: string) => void;
  onComponentDeleted: (componentId: string) => void;
  onEditComponent: (componentId: string) => void;
  isEditing: boolean;
  isComponentEditingEnabled?: boolean;
  canDelete: boolean;
  showMinimized?: boolean;
}> = ({
  component,
  onComponentDropped,
  onComponentDeleted,
  onEditComponent,
  canDelete,
  isEditing,
  isComponentEditingEnabled,
  showMinimized,
}) => {
  // eslint-disable-next-line react-hooks/static-components
  const C = component?.render();
  const [style, setStyle] = useState<HTMLAttributes<HTMLDivElement>["style"]>();
  const [isDragging, setIsDragging] = useState(false);
  const editorHighlightColor = useEditorHighlightColor();

  return (
    <div
      data-pimo-dnd-column
      onDragOver={(e) => {
        e.preventDefault();
        setStyle({ background: "#e8e8e8" });
      }}
      onDragLeave={() => setStyle({ background: "#fff" })}
      onDrop={(e) => {
        e.preventDefault();
        setStyle({ background: "#fff" });
        const componentId = e.dataTransfer.getData("componentId");
        if (!componentId) {
          return;
        }

        onComponentDropped?.(componentId);
      }}
      onDragEnd={() => {
        setIsDragging(false);
      }}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        width: "100%",
        height: "100%",
        minHeight: !showMinimized ? "50px" : "",
        background: "#fff",
        borderRadius: "10px",
        ...style,
      }}
    >
      {isEditing && !isDragging && C && (
        <div
          style={{
            position: "absolute",
            zIndex: 100,
            top: "-15px",
            right: "-15px",
            padding: "5px",
            display: "flex",
            flexDirection: "row",
            gap: "10px",
          }}
        >
          {isComponentEditingEnabled && (
            <div
              style={{
                cursor: "pointer",
                background: editorHighlightColor,
                color: "#fff",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={() => {
                if (!component?.layoutProps?.componentId) {
                  return;
                }

                onEditComponent(component.layoutProps.componentId);
              }}
            >
              <Edit />
            </div>
          )}

          {canDelete && (
            <div
              style={{
                cursor: "pointer",
                background: editorHighlightColor,
                color: "#fff",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={() => {
                if (!component?.layoutProps?.componentId) {
                  return;
                }

                onComponentDeleted(component.layoutProps.componentId);
              }}
            >
              <Delete />
            </div>
          )}
        </div>
      )}
      <div
        draggable={isEditing}
        onDragStart={(e) => {
          setIsDragging(true);
          e.dataTransfer.setData(
            "componentId",
            component?.layoutProps?.componentId ?? ""
          );
        }}
        style={{
          zoom: showMinimized ? 0.3 : 1,
          height: showMinimized ? undefined : "100%",
        }}
      >
        {C ? (
          <div
            data-component-id={component?.layoutProps?.componentId}
            style={{
              display: "flex",
              height: showMinimized ? undefined : "100%",
              width: "100%",
            }}
          >
            {/* eslint-disable-next-line react-hooks/static-components */}
            <C />
          </div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};
